import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
    CHUNK_RELOAD_GRACE_MS,
    isReloadingForChunk
} from '../features/pwa/appUpdate';
import { bootLines, deviceLines } from '../features/support/diagnostics';
import {
    reportProblem,
    type ReportState
} from '../features/support/problemReport';

/**
 * The last thing between a crash and a white screen.
 *
 * A component that threw used to take the whole tree with it: React unmounts
 * everything and leaves an empty page, with the reason in a console nobody in a
 * trench is going to open - the person cannot even say WHAT went wrong, because
 * there is nothing left on screen to describe. So it is caught here, said out
 * loud, and reported by the app itself.
 *
 * Deliberately plain: no MUI, no translation, no theme, no router. Everything
 * this renders has to work when the thing that just broke might BE the theme,
 * the language or the route.
 */
interface AppErrorBoundaryState {
    error: Error | null;
    reported: ReportState | null;
    /** The crash came while a reload for a new version was under way. */
    waitingForReload: boolean;
}

export default class AppErrorBoundary extends Component<
    { children: ReactNode },
    AppErrorBoundaryState
> {
    state: AppErrorBoundaryState = {
        error: null,
        reported: null,
        waitingForReload: false
    };

    static getDerivedStateFromError(error: unknown) {
        return {
            error: error instanceof Error ? error : new Error(String(error))
        };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('[App] crashed', error, info.componentStack);
        if (!isReloadingForChunk()) {
            this.report(error, info);
            return;
        }
        // Most likely the reload lands and this page is gone. If it is still
        // here after the grace period, the reload was cancelled and the crash
        // is shown and reported like any other.
        this.setState({ waitingForReload: true });
        window.setTimeout(() => {
            this.setState({ waitingForReload: false });
            this.report(error, info);
        }, CHUNK_RELOAD_GRACE_MS);
    }

    private report(error: Error, info: ErrorInfo) {
        this.setState({ reported: 'sending' });

        // The message says what threw, the component stack says where. The
        // FIRST few frames only - the rest is providers, identical in every
        // crash.
        const where = (info.componentStack ?? '')
            .split('\n')
            .filter((line) => line.trim() !== '')
            .slice(0, 8)
            .join('\n');

        void reportProblem({
            kind: 'crash',
            headline: `Absturz: ${error.message}`.slice(0, 200),
            /**
             * Neither the message nor the top frames move between occurrences:
             * that is what makes the same crash on twenty devices one fault.
             */
            signature: `crash|${error.message}|${where.split('\n')[0] ?? ''}`,
            body: [
                `Absturz: ${error.message}`,
                '',
                ...deviceLines(),
                ...bootLines(),
                '',
                error.stack ?? '(kein Stack)',
                '',
                'Komponenten:',
                where
            ].join('\n')
        }).then((reported) => this.setState({ reported }));
    }

    render() {
        const { error, reported } = this.state;
        if (!error) {
            return this.props.children;
        }
        if (this.state.waitingForReload) {
            return (
                <div
                    style={{
                        padding: 24,
                        fontFamily: 'system-ui, sans-serif',
                        textAlign: 'center',
                        color: '#555'
                    }}
                >
                    <p>Neue Version wird geladen …</p>
                    <button
                        onClick={() => location.reload()}
                        style={{
                            padding: '8px 16px',
                            fontSize: 15,
                            cursor: 'pointer'
                        }}
                    >
                        Neu laden
                    </button>
                </div>
            );
        }
        return (
            <div
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 16,
                    padding: 24,
                    fontFamily: 'system-ui, sans-serif',
                    textAlign: 'center'
                }}
            >
                <h2 style={{ margin: 0 }}>Da ist etwas schiefgelaufen</h2>
                <p style={{ maxWidth: 520, color: '#555' }}>
                    Ihre Aufnahmen liegen auf dem Gerät und gehen nicht verloren
                    - sie werden übertragen, sobald die App wieder läuft. Meist
                    genügt ein neuer Versuch.
                </p>
                {reported && reported !== 'off' ? (
                    <p
                        style={{
                            maxWidth: 520,
                            color: '#777',
                            fontSize: 13,
                            margin: 0
                        }}
                    >
                        {reported === 'sending'
                            ? 'Ein Fehlerbericht wird an die Entwicklung geschickt …'
                            : reported === 'sent'
                              ? 'Ein Fehlerbericht wurde an die Entwicklung geschickt.'
                              : 'Ein Fehlerbericht ist vorgemerkt und geht raus, sobald wieder eine Verbindung besteht.'}
                    </p>
                ) : null}
                <button
                    onClick={() => location.reload()}
                    style={{
                        padding: '10px 18px',
                        fontSize: 15,
                        cursor: 'pointer',
                        border: '1px solid #1976d2',
                        background: '#1976d2',
                        color: '#fff',
                        borderRadius: 6
                    }}
                >
                    Nochmal versuchen
                </button>
                {/* The report goes to the developers, but the office is who
                    can get somebody working again the same day. */}
                <p style={{ maxWidth: 520, color: '#555', margin: 0 }}>
                    Hilft auch Neuladen nicht, bitte im Büro melden.
                </p>
                <pre
                    style={{
                        maxWidth: 520,
                        whiteSpace: 'pre-wrap',
                        color: '#999',
                        fontSize: 12
                    }}
                >
                    {`Version ${__APP_VERSION__}\n${error.message}`}
                </pre>
            </div>
        );
    }
}
